import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { PasswordPolicyMsg } from './password-policy.messages';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 30;

const MIN_FRAGMENT_LENGTH = 3;
const MIN_NAME_PART_LENGTH = 3;
const MAX_DIGIT_SEQUENCE_RUN = 2;
const MAX_LETTER_SEQUENCE_RUN = 3;
const MAX_REPEAT_RUN = 2;

const UPPERCASE = /[A-Z]/;
const LOWERCASE = /[a-z]/;
const DIGIT = /[0-9]/;
const SPECIAL = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]/;
const ALLOWED_CHARS = /^[A-Za-z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?~`]+$/;

const KEYBOARD_PATTERNS = [
  'qwerty', 'qwert', 'werty', 'asdfg', 'asdf', 'zxcvb', 'zxcv',
  'qazwsx', 'wasd', '1qaz', '2wsx', 'poiuy', 'lkjh', 'mnbv'
];

const COMMON_PASSWORDS = [
  "password", "passw0rd", "p@ssword", "p@ssw0rd", "welcome", "admin",
  "letmein", "iloveyou", "monkey", "dragon", "sunshine", "princess", "tax",
  "football", "baseball", "superman", "trustno1", "bangladesh", "ereturn",
  "return", "income", "filing", "ledger", "tds", "law", "itp",
  "nbr", "taxpayer", "tin"
];

const GENERIC_DOMAIN_LABELS = [
  'com', 'net', 'org', 'edu', 'gov', 'info', 'mail', 'co', 'bd', 'www',
  'gmail', 'yahoo', 'hotmail', 'outlook', 'live', 'icloud'
];

export interface PasswordPolicyHint {
  key: string;
  label: string;
  requiredWhenEmpty: boolean;
}

export const PASSWORD_POLICY_HINTS: PasswordPolicyHint[] = [
  { key: 'minLength', label: 'At least 8 characters', requiredWhenEmpty: true },
  { key: 'maxLength', label: 'At most 30 characters', requiredWhenEmpty: false },
  { key: 'hasCapitalCase', label: 'At least 1 capital letter', requiredWhenEmpty: true },
  { key: 'hasSmallCase', label: 'At least 1 small letter', requiredWhenEmpty: true },
  { key: 'hasNumber', label: 'At least 1 number', requiredWhenEmpty: true },
  { key: 'hasSpecialCharacters', label: 'At least 1 special character (such as !@#$%^&*)', requiredWhenEmpty: true },
  { key: 'noPersonalInfo', label: "Don't use any personal information . (Name, Email, Mobile, TIN, DoB)", requiredWhenEmpty: false },
  { key: 'noCommonSequence', label: "Don't use common sequence (such as 123, 321, abcd, dcba, 111, aaa)", requiredWhenEmpty: false },
  { key: 'noKeyboardOrCommonWords', label: "Don't use keyboard pattern (such as qwerty or asdf) or any general words (such as admin, password etc.).", requiredWhenEmpty: false },
  { key: 'notSameAsOld', label: 'New Password cannot be same as your Old Password!', requiredWhenEmpty: false }
];

export const PASSWORD_POLICY_PUBLIC_HINTS: PasswordPolicyHint[] = PASSWORD_POLICY_HINTS.filter(function (hint) {
  return hint.key !== 'notSameAsOld';
});

export const PERSONAL_INFO_HINT_PARTS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'tin', label: 'TIN' },
  { key: 'dob', label: 'DoB' }
];

export class PasswordPolicyIdentity {
  values: string[] = [];
  labels: string[] = [];
  kinds: string[] = [];
  wholeOnly: boolean[] = [];

  private add(value: string, label: string, kind: string, wholeTokenOnly = false): PasswordPolicyIdentity {
    if (value != null && String(value).trim() !== '') {
      this.values.push(String(value).trim());
      this.labels.push(label);
      this.kinds.push(kind);
      this.wholeOnly.push(wholeTokenOnly);
    }
    return this;
  }

  name(name: string): PasswordPolicyIdentity {
    if (name == null) {
      return this;
    }
    const parts = String(name).trim().split(/\s+/);
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part && part.length >= MIN_NAME_PART_LENGTH) {
        this.add(part, PasswordPolicyMsg.passContainsNameErrMsg, 'name', true);
      }
    }
    return this;
  }

  phone(phone: string): PasswordPolicyIdentity {
    if (!phone) {
      return this;
    }
    const digits = String(phone).replace(/\D/g, '');
    this.add(digits, PasswordPolicyMsg.passContainsPhoneErrMsg, 'mobile');
    if (digits.indexOf('880') === 0) {
      this.add(digits.substring(3), PasswordPolicyMsg.passContainsPhoneErrMsg, 'mobile');
    } else if (digits.indexOf('0') === 0) {
      this.add(digits.substring(1), PasswordPolicyMsg.passContainsPhoneErrMsg, 'mobile');
    }
    return this;
  }

  email(email: string): PasswordPolicyIdentity {
    if (email && email.indexOf('@') >= 0) {
      const local = email.substring(0, email.indexOf('@'));
      const domain = email.substring(email.indexOf('@') + 1);
      this.add(local, PasswordPolicyMsg.passContainsEmailErrMsg, 'email');
      local.split(/[._\-+]/).forEach((part) => {
        this.add(part, PasswordPolicyMsg.passContainsEmailErrMsg, 'email');
      });
      domain.split('.').forEach((label) => {
        if (label && GENERIC_DOMAIN_LABELS.indexOf(label.toLowerCase()) < 0) {
          this.add(label, PasswordPolicyMsg.passContainsEmailErrMsg, 'email');
        }
      });
    } else {
      this.add(email, PasswordPolicyMsg.passContainsEmailErrMsg, 'email');
    }
    return this;
  }

  tin(tin: string): PasswordPolicyIdentity {
    if (tin == null || String(tin).trim() === '') {
      return this;
    }
    return this.add(String(tin).replace(/\s/g, ''), PasswordPolicyMsg.passContainsTinErrMsg, 'tin');
  }

  dateOfBirth(dateOfBirth: Date | string): PasswordPolicyIdentity {
    const date = parseDateOfBirth(dateOfBirth);
    if (!date) {
      return this;
    }

    const yyyy = String(date.getFullYear());
    const yy = yyyy.substring(2);
    const mm = ('0' + (date.getMonth() + 1)).slice(-2);
    const dd = ('0' + date.getDate()).slice(-2);

    [dd + mm + yyyy, mm + dd + yyyy, yyyy + mm + dd, yyyy + dd + mm,
      dd + mm + yy, mm + dd + yy, yy + mm + dd,
      dd + mm, mm + dd, mm + yyyy, dd + yyyy].forEach((form) => {
      this.add(form, PasswordPolicyMsg.passContainsDobErrMsg, 'dob', true);
    });

    this.add(yyyy, PasswordPolicyMsg.passContainsBirthYearErrMsg, 'dob', true);
    return this;
  }

  static fromAvailableUserData(data?: {
    name?: string;
    userName?: string;
    phone?: string;
    email?: string;
    tin?: string;
    dateOfBirth?: Date | string;
  }): PasswordPolicyIdentity {
    const identity = new PasswordPolicyIdentity();
    const source = data || {};
    identity.name(source.name);
    identity.phone(source.phone);
    identity.email(source.email);
    identity.tin(source.tin);
    identity.dateOfBirth(source.dateOfBirth);
    return identity;
  }

  static fromOtpVerifyResult(result: any, fallback?: {
    name?: string;
    userName?: string;
    phone?: string;
    email?: string;
    tin?: string;
    dateOfBirth?: Date | string;
  }): PasswordPolicyIdentity {
    const payload = flattenOtpVerifyPayload(result);
    const source = fallback || {};
    return PasswordPolicyIdentity.fromAvailableUserData({
      name: pickIdentityText(payload.assesName, payload.name, payload.taxpayerName, payload.fullName, source.name),
      userName: pickIdentityText(payload.userName, payload.username, payload.tin, source.userName, source.tin),
      tin: pickIdentityText(payload.tin, payload.userIdentification, payload.etin, source.tin),
      phone: pickIdentityText(payload.phone, payload.mobile, payload.mobileNumber, payload.userMobileNo, source.phone),
      email: pickIdentityText(payload.email, payload.emailAddress, source.email),
      dateOfBirth: pickIdentityValue(payload.dob, payload.dateOfBirth, payload.birthDate, source.dateOfBirth)
    });
  }
}

export class PasswordPolicyValidator {
  static evaluate(password: string, identity?: PasswordPolicyIdentity, currentPassword?: string): ValidationErrors {
    const errors: ValidationErrors = {};

    if (password == null || password === '') {
      errors.required = true;
      errors.minLength = true;
      errors.hasCapitalCase = true;
      errors.hasSmallCase = true;
      errors.hasNumber = true;
      errors.hasSpecialCharacters = true;
      return errors;
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      errors.minLength = true;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      errors.maxLength = true;
    }
    if (/\s/.test(password)) {
      errors.whitespace = true;
    }
    if (!ALLOWED_CHARS.test(password)) {
      errors.illegalChar = true;
    }
    if (!UPPERCASE.test(password)) {
      errors.hasCapitalCase = true;
    }
    if (!LOWERCASE.test(password)) {
      errors.hasSmallCase = true;
    }
    if (!DIGIT.test(password)) {
      errors.hasNumber = true;
    }
    if (!SPECIAL.test(password)) {
      errors.hasSpecialCharacters = true;
    }

    const lower = password.toLowerCase();

    if (hasCommonPassword(lower)) {
      errors.noKeyboardOrCommonWords = true;
    }
    if (hasRepeatedRun(lower)) {
      errors.noCommonSequence = true;
    }
    if (sequenceViolation(lower)) {
      errors.noCommonSequence = true;
    }
    if (hasKeyboardPattern(lower)) {
      errors.noKeyboardOrCommonWords = true;
    }
    if (hasPersonalInfo(lower, identity)) {
      errors.noPersonalInfo = true;
    }
    if (isSameAsCurrentPassword(password, currentPassword)) {
      errors.notSameAsOld = true;
    }

    return errors;
  }

  static matchedPersonalKinds(password: string, identity?: PasswordPolicyIdentity): string[] {
    const matched: string[] = [];
    if (!password || !identity || !identity.values) {
      return matched;
    }
    const lower = password.toLowerCase();
    const seen: { [key: string]: boolean } = {};
    const kinds = identity.kinds || [];
    const labels = identity.labels || [];
    const wholeOnly = identity.wholeOnly || [];
    for (let i = 0; i < identity.values.length; i++) {
      const kind = kinds[i] || kindFromPersonalLabel(labels[i]);
      if (!kind || seen[kind]) {
        continue;
      }
      const violated = wholeOnly[i]
        ? lower.indexOf(String(identity.values[i]).toLowerCase()) >= 0
        : containsFragmentOf(lower, identity.values[i]);
      if (violated) {
        seen[kind] = true;
        matched.push(kind);
      }
    }
    return matched;
  }

  static validate(password: string, identity?: PasswordPolicyIdentity, currentPassword?: string): string | null {
    if (password == null || password === '') {
      return PasswordPolicyMsg.passRequiredErrMsg;
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
      return PasswordPolicyMsg.passTooShortErrMsg;
    }
    if (password.length > PASSWORD_MAX_LENGTH) {
      return PasswordPolicyMsg.passTooLongErrMsg;
    }
    if (/\s/.test(password)) {
      return PasswordPolicyMsg.passWhitespaceErrMsg;
    }
    if (!ALLOWED_CHARS.test(password)) {
      return PasswordPolicyMsg.passIllegalCharErrMsg;
    }
    if (!UPPERCASE.test(password)) {
      return PasswordPolicyMsg.passNoUppercaseErrMsg;
    }
    if (!LOWERCASE.test(password)) {
      return PasswordPolicyMsg.passNoLowercaseErrMsg;
    }
    if (!DIGIT.test(password)) {
      return PasswordPolicyMsg.passNoDigitErrMsg;
    }
    if (!SPECIAL.test(password)) {
      return PasswordPolicyMsg.passNoSpecialCharErrMsg;
    }

    const lower = password.toLowerCase();
    if (hasCommonPassword(lower)) {
      return PasswordPolicyMsg.passCommonlyUsedErrMsg;
    }
    if (hasRepeatedRun(lower)) {
      return PasswordPolicyMsg.passRepeatedCharErrMsg;
    }
    const sequenceError = sequenceViolation(lower);
    if (sequenceError) {
      return sequenceError;
    }
    if (hasKeyboardPattern(lower)) {
      return PasswordPolicyMsg.passKeyboardPatternErrMsg;
    }
    const personalError = personalInfoViolation(lower, identity);
    if (personalError) {
      return personalError;
    }
    if (isSameAsCurrentPassword(password, currentPassword)) {
      return PasswordPolicyMsg.passSameAsOldErrMsg;
    }
    return null;
  }

  static angular(identity?: PasswordPolicyIdentity): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }
      const currentPassword = getSiblingOldPassword(control);
      const errors = PasswordPolicyValidator.evaluate(String(control.value), identity, currentPassword);
      return Object.keys(errors).length ? errors : null;
    };
  }

  static assertMatch(password: string, confirmPassword: string): string | null {
    if (password !== confirmPassword) {
      return PasswordPolicyMsg.passMismatchErrMsg;
    }
    return null;
  }

  static matchConfirmPassword(group: AbstractControl): ValidationErrors | null {
    if (!group) {
      return null;
    }
    const passwordCtrl = group.get('password') || group.get('newPass');
    const confirmCtrl = group.get('confirmpassword') || group.get('confirmPassword') || group.get('retypedNewPass');
    if (!passwordCtrl || !confirmCtrl) {
      return null;
    }
    const confirm = confirmCtrl.value;
    if (confirm == null || confirm === '') {
      return null;
    }
    if (passwordCtrl.value !== confirm) {
      const errors = confirmCtrl.errors ? Object.assign({}, confirmCtrl.errors) : {};
      errors.passwordMismatch = true;
      confirmCtrl.setErrors(errors);
      return { passwordMismatch: true };
    }
    if (confirmCtrl.errors && confirmCtrl.errors.passwordMismatch) {
      const rest = Object.assign({}, confirmCtrl.errors);
      delete rest.passwordMismatch;
      confirmCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }
    return null;
  }
}

function getSiblingOldPassword(control: AbstractControl): string {
  const parent = control.parent;
  if (!parent) {
    return '';
  }
  const oldPasswordControl = parent.get('oldPassword');
  if (!oldPasswordControl || oldPasswordControl.value == null || oldPasswordControl.value === '') {
    return '';
  }
  return String(oldPasswordControl.value);
}

function isSameAsCurrentPassword(password: string, currentPassword?: string): boolean {
  return !!currentPassword && password === currentPassword;
}

function hasCommonPassword(lowerPassword: string): boolean {
  for (let i = 0; i < COMMON_PASSWORDS.length; i++) {
    const common = COMMON_PASSWORDS[i];
    if (common.length >= 3 ? lowerPassword.indexOf(common) >= 0 : lowerPassword === common) {
      return true;
    }
  }
  return false;
}

function hasKeyboardPattern(lowerPassword: string): boolean {
  for (let i = 0; i < KEYBOARD_PATTERNS.length; i++) {
    const pattern = KEYBOARD_PATTERNS[i];
    if (lowerPassword.indexOf(pattern) >= 0 || lowerPassword.indexOf(reverse(pattern)) >= 0) {
      return true;
    }
  }
  return false;
}

function hasPersonalInfo(lowerPassword: string, identity?: PasswordPolicyIdentity): boolean {
  return !!personalInfoViolation(lowerPassword, identity);
}

function kindFromPersonalLabel(label: string): string {
  if (!label) {
    return '';
  }
  if (label === PasswordPolicyMsg.passContainsNameErrMsg || label === PasswordPolicyMsg.passContainsUserNameErrMsg) {
    return 'name';
  }
  if (label === PasswordPolicyMsg.passContainsEmailErrMsg) {
    return 'email';
  }
  if (label === PasswordPolicyMsg.passContainsPhoneErrMsg) {
    return 'mobile';
  }
  if (label === PasswordPolicyMsg.passContainsTinErrMsg) {
    return 'tin';
  }
  if (label === PasswordPolicyMsg.passContainsDobErrMsg || label === PasswordPolicyMsg.passContainsBirthYearErrMsg) {
    return 'dob';
  }
  return '';
}

function personalInfoViolation(lowerPassword: string, identity?: PasswordPolicyIdentity): string | null {
  if (!identity) {
    return null;
  }
  for (let i = 0; i < identity.values.length; i++) {
    const value = identity.values[i];
    const violated = identity.wholeOnly[i]
      ? lowerPassword.indexOf(value.toLowerCase()) >= 0
      : containsFragmentOf(lowerPassword, value);
    if (violated) {
      return identity.labels[i];
    }
  }
  return null;
}

function containsFragmentOf(lowerPassword: string, personalValue: string): boolean {
  if (!personalValue) {
    return false;
  }
  const tokens = personalValue.toLowerCase().split(/[^a-z0-9]+/);
  for (let t = 0; t < tokens.length; t++) {
    const token = tokens[t];
    if (token.length < MIN_FRAGMENT_LENGTH) {
      continue;
    }
    const reversed = reverse(token);
    for (let start = 0; start + MIN_FRAGMENT_LENGTH <= token.length; start++) {
      for (let end = start + MIN_FRAGMENT_LENGTH; end <= token.length; end++) {
        if (lowerPassword.indexOf(token.substring(start, end)) >= 0
          || lowerPassword.indexOf(reversed.substring(start, end)) >= 0) {
          return true;
        }
      }
    }
  }
  return false;
}

function hasRepeatedRun(lowerPassword: string): boolean {
  let run = 1;
  for (let i = 1; i < lowerPassword.length; i++) {
    if (lowerPassword.charAt(i) === lowerPassword.charAt(i - 1)) {
      run++;
      if (run > MAX_REPEAT_RUN) {
        return true;
      }
    } else {
      run = 1;
    }
  }
  return false;
}

function sequenceViolation(lowerPassword: string): string | null {
  let ascending = 1;
  let descending = 1;

  for (let i = 1; i < lowerPassword.length; i++) {
    const previous = lowerPassword.charAt(i - 1);
    const current = lowerPassword.charAt(i);
    const digitRun = isDigit(previous) && isDigit(current);
    const letterRun = isLetter(previous) && isLetter(current);

    if ((digitRun || letterRun) && current.charCodeAt(0) - previous.charCodeAt(0) === 1) {
      ascending++;
      descending = 1;
    } else if ((digitRun || letterRun) && previous.charCodeAt(0) - current.charCodeAt(0) === 1) {
      descending++;
      ascending = 1;
    } else {
      ascending = 1;
      descending = 1;
      continue;
    }

    const limit = digitRun ? MAX_DIGIT_SEQUENCE_RUN : MAX_LETTER_SEQUENCE_RUN;
    if (ascending > limit || descending > limit) {
      return digitRun
        ? PasswordPolicyMsg.passNumericSequenceErrMsg
        : PasswordPolicyMsg.passAlphabeticSequenceErrMsg;
    }
  }

  return null;
}

function isDigit(char: string): boolean {
  return char >= '0' && char <= '9';
}

function isLetter(char: string): boolean {
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z');
}

function reverse(value: string): string {
  return value.split('').reverse().join('');
}

function flattenOtpVerifyPayload(result: any): any {
  const merged: any = {};
  if (!result || typeof result !== 'object') {
    return merged;
  }
  assignIdentityFields(merged, result);
  const nestedKeys = ['replyMessage', 'data', 'personalInfo', 'taxpayerInfo', 'taxpayer', 'user'];
  for (let i = 0; i < nestedKeys.length; i++) {
    const nested = result[nestedKeys[i]];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      assignIdentityFields(merged, nested);
      for (let j = 0; j < nestedKeys.length; j++) {
        const inner = nested[nestedKeys[j]];
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
          assignIdentityFields(merged, inner);
        }
      }
    }
  }
  return merged;
}

function assignIdentityFields(target: any, source: any): void {
  if (!source || typeof source !== 'object') {
    return;
  }
  const keys = [
    'assesName', 'name', 'taxpayerName', 'fullName',
    'userName', 'username',
    'tin', 'userIdentification', 'etin',
    'phone', 'mobile', 'mobileNumber', 'userMobileNo',
    'email', 'emailAddress',
    'dob', 'dateOfBirth', 'birthDate',
    'serialNo'
  ];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (source[key] != null && source[key] !== '') {
      target[key] = source[key];
    }
  }
}

function pickIdentityText(...values: any[]): string {
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function pickIdentityValue(...values: any[]): any {
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value != null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
}

function parseDateOfBirth(value: Date | string): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const text = String(value).trim();
  const iso = Date.parse(text);
  if (!isNaN(iso)) {
    return new Date(iso);
  }

  const dmy = text.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
      return date;
    }
  }

  return null;
}
